import { Avatar, MessageBubble, MessageFile, MessageImage, MessageText } from '@engyalo/design-system';
import { Icon, Stack, Tooltip } from '@mui/material';
import parse from 'html-react-parser';
import i18n from 'i18n';
import { formatMessageTime } from 'helpers/formatterDates';
import { parseLinksToAnchorTags } from '../utils/parseLinksToAnchorsTags';
import formatTextToHTML from '../utils/formatTextToHTML';
import { INotificationMessageProps, MEDIA_TYPE, TypeAction } from './types';
import { CTActionButtonWrapper, CustomIcon, CustomTypography } from './style';

export const ICONS = {
  [TypeAction.URL]: 'fa-up-right-from-square',
  [TypeAction.PHONE_NUMBER]: 'fa-phone',
  [TypeAction.LIST]: 'fa-list',
  [TypeAction.QUICK_REPLY]: 'fa-reply',
};

const NotificationMessage = ({ TemplateID, createdAt, Header, Body, Footer, Buttons }: INotificationMessageProps) => {
  const testId = `notification-message-${TemplateID}`;
  const invalidImage = 'https://invalid-url.com/invalid-image.jpg';
  const htmlContent = formatTextToHTML(Body.Text || '');
  const bodyContentParsedURL = parseLinksToAnchorTags(htmlContent);

  const getHeaderMedia = () => {
    if (Header?.Media?.Type === MEDIA_TYPE.IMAGE && Header?.Media?.Link) {
      return <MessageImage src={Header.Media.Link || invalidImage} alt={Header.Media.Caption || ''} />;
    }
    if (Header?.Media?.Type === MEDIA_TYPE.DOCUMENT && Header?.Media?.Link) {
      return <MessageFile url={Header.Media.Link} mimeType="application/pdf" />;
    }
    return null;
  };

  const sentAtDate = formatMessageTime(createdAt, i18n.language);

  return (
    <MessageBubble
      data-testid={testId}
      variant="agent"
      status="sent"
      avatar={<Avatar variant="icon" customIcon={<Icon className="fa-solid fa-message-bot" fontSize="inherit" />} />}
      sentAt={sentAtDate}
      disableAutoGroup
    >
      {getHeaderMedia()}
      <Stack padding="0.4rem">
        <MessageText
          body={Body.Text ? <span style={{ whiteSpace: 'pre-line' }}>{parse(bodyContentParsedURL)}</span> : ''}
          bodyLength={bodyContentParsedURL?.length}
          header={Header?.Text}
          footer={Footer?.Text}
        />
      </Stack>
      {Buttons?.map((button) => {
        const buttonText = button?.Text || '';
        const buttonUrl = button?.URL || '';
        const buttonPhoneNumber = button?.PhoneNumber || '';
        const buttonType = button?.Type || '';
        const icon = ICONS[buttonType as keyof typeof ICONS];
        const tooltipText = buttonUrl || buttonPhoneNumber || buttonText || '';
        return (
          <Tooltip title={tooltipText} placement="left">
            <CTActionButtonWrapper disableFocusRipple aria-label={tooltipText}>
              {icon ? <CustomIcon data-testid={icon} className={`fa-solid ${icon}`} /> : null}
              <CustomTypography variant="body1">{buttonText}</CustomTypography>
            </CTActionButtonWrapper>
          </Tooltip>
        );
      })}
    </MessageBubble>
  );
};

export default NotificationMessage;
